import EcoSpace from 'eco-design/base/eco-space';
import EcoAccessories, { EcoAccessoriesItemProps } from 'eco-design/sections-test/eco-accessories';
import { useMemo } from 'react';
import { ShopifyCartItemType } from '@elem/compCommon/ShopifyShoppingCart/const';
import styles from './index.module.scss';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/i18n';
import useProductInfo from '@/utils/hooks/useProductInfo';

type ItemsType = (EcoAccessoriesItemProps & {
    productId: number;
    variantId: number;
})[];

export default function Comp({ callback }: { callback?: (card: ShopifyCartItemType[]) => void }) {
    const { t } = useLang('theProduct');
    const { locale = 'us' } = getLocal();

    const IS_NA_SITE = ['us', 'ca'].includes(locale);
    const IS_JP_SITE = locale === 'jp';

    const itemsA: ItemsType = [
        (IS_NA_SITE || IS_JP_SITE) && {
            title: t('P_item0_title'),
            desc: t('P_item0_desc'),
            productId: (() => {
                const productIds = {
                    us: 15313905287241,
                    ca: 15075198271859,
                    jp: 14692433887599,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54386591006793,
                    ca: 52505080988019,
                    jp: 51841130365295,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item1_title'),
            desc: t('P_item1_desc'),
            productId: (() => {
                const productIds = {
                    us: 15299454173257,
                    ca: 15069853352307,
                    jp: 8055652941985,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54334704549961,
                    ca: 52475379122547,
                    jp: 44394781507745,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item2_title'),
            desc: t('P_item2_desc'),
            productId: (() => {
                const productIds = {
                    us: 15298922250313,
                    ca: 15069849616755,
                    jp: 14692240458095,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54332956311625,
                    ca: 52475374272883,
                    jp: 51840093487471,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item3_title'),
            desc: t('P_item3_desc'),
            productId: (() => {
                const productIds = {
                    us: 15298917007433,
                    ca: 15069848764787,
                    jp: 14692240621935,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54332950478921,
                    ca: 52475372208499,
                    jp: 51840094372207,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item4_title'),
            desc: t('P_item4_desc'),
            productId: (() => {
                const productIds = {
                    us: 6749070688329,
                    ca: 7399325139098,
                    jp: 14692240654703,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 40541578330185,
                    ca: 44741345837210,
                    jp: 51840094404975,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item5_title'),
            desc: t('P_item5_desc'),
            productId: (() => {
                const productIds = {
                    us: 15302606553161,
                    ca: 7790321598618,
                    jp: 7701210333345,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54347637293129,
                    ca: 52475346911603,
                    jp: 52142147305839,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
        {
            title: t('P_item6_title'),
            desc: t('P_item6_desc'),
            productId: (() => {
                const productIds = {
                    us: 15302621429833,
                    ca: 15069856006515,
                    jp: 7731994984609,
                };
                return productIds[locale as 'us'] || productIds.us;
            })(),
            variantId: (() => {
                const variantIds = {
                    us: 54347678318665,
                    ca: 52475403731315,
                    jp: 43113018720417,
                };
                return variantIds[locale as 'us'] || variantIds.us;
            })(),
        },
    ].filter(Boolean) as ItemsType;

    const productInfo = useProductInfo(itemsA, {
        variantImg: 'pc',
        skuTitle: 'title',
        compareAtPrice: 'comparePrice',
    });
    const list = useMemo(
        () =>
            productInfo.map((i) => {
                const title = i.title === 'Default Title' ? i.productTitle : i.title ?? '';
                return {
                    ...i,
                    title,
                    ...((i?.inventoryQuantity ?? 0) <= 0 ? { lDisabled: true } : {}),
                    lChildren: t('A_info_btn'),
                    lTheme: 'blue',
                    lType: 'filled',
                    noPrefix: true,
                    ...(IS_JP_SITE
                        ? {
                            onClick() {
                                callback?.([
                                    {
                                        originalPrice: i.compareAtPrice,
                                        discountPrice: i.price,
                                        img: i.variantImg,
                                        productImg: i.img,
                                        title,
                                        productId: String(i.productId),
                                        variantId: String(i.variantId),
                                        quantity: 1,
                                        inventory: i.inventoryQuantity,
                                    },
                                ]);
                            },
                            lDataLayer: {
                                event: 'uaEvent',
                                event_name: 'bundle_add_to_cart',
                                list_name: t('P_title'),
                                product_name: title,
                                price: i.price,
                                tag: 'none',
                                quantity: 1,
                                button_name: t('A_info_btn'),
                            },
                        }
                        : {
                            lHref: `https://${locale}.ecoflow.com/products/${i.handle}`,
                            lDataLayer: {
                                event: 'uaEvent',
                                event_name: 'buy',
                                product_name: i.title,
                                position: `Product Detail_Accessories`,
                            },
                        }),
                };
            }),
        [productInfo],
    );

    return (
        <div className={styles.comp} id="AccessoriesA">
            <EcoSpace />
            <EcoAccessories title={t('P_title')} items={list as any} />
        </div>
    );
}
